import { I18nService } from '../../i18n/I18nService';
import { I18N_KEYS } from '../../i18n/keys';

export class CurrentNumberDisplay {
  private i18n: I18nService;
  private element: HTMLElement;
  private numberEl: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private progressEl: HTMLElement | null = null;
  private skipBtn: HTMLButtonElement | null = null;

  onSkipAudio: (() => void) | null = null;

  constructor(i18n: I18nService) {
    this.i18n = i18n;
    this.element = document.getElementById('current-number-display') || document.createElement('section');
  }

  render(): void {
    this.element.innerHTML = `
      <div class="display__container">
        <div id="display-status" class="display__status"></div>
        <div id="display-number" class="display__number" aria-live="assertive" aria-atomic="true">
          <span class="visually-hidden">Current number:</span>
          <span class="display__number-text">${this.i18n.t(I18N_KEYS.DISPLAY_READY)}</span>
        </div>
        <div id="display-progress" class="display__progress" style="display:none;">
          <div class="display__progress-bar"><div class="display__progress-fill"></div></div>
        </div>
        <button id="skip-audio-btn" class="display__skip-btn" style="display:none;" type="button">
          ${this.i18n.t(I18N_KEYS.DISPLAY_SKIP_AUDIO)}
        </button>
      </div>
    `;

    this.numberEl = this.element.querySelector('.display__number-text');
    this.statusEl = this.element.querySelector('#display-status');
    this.progressEl = this.element.querySelector('#display-progress');
    this.skipBtn = this.element.querySelector('#skip-audio-btn');

    this.skipBtn?.addEventListener('click', () => {
      if (this.onSkipAudio) this.onSkipAudio();
    });
  }

  showReady(): void {
    if (this.numberEl) {
      this.numberEl.textContent = this.i18n.t(I18N_KEYS.DISPLAY_READY);
      this.numberEl.className = 'display__number-text display__number-text--ready';
    }
    if (this.statusEl) this.statusEl.textContent = '';
    this.hideProgress();
    this.hideSkipButton();
  }

  showPlaying(): void {
    if (this.statusEl) {
      this.statusEl.textContent = this.i18n.t(I18N_KEYS.DISPLAY_PLAYING);
      this.statusEl.className = 'display__status display__status--playing';
    }
    if (this.numberEl) {
      this.numberEl.textContent = '??';
      this.numberEl.className = 'display__number-text display__number-text--playing';
    }
  }

  showNumber(number: number): void {
    if (this.numberEl) {
      this.numberEl.textContent = String(number);
      this.numberEl.className = 'display__number-text display__number-text--current';
    }
    if (this.statusEl) {
      this.statusEl.textContent = '';
      this.statusEl.className = 'display__status';
    }
    this.hideProgress();
    this.hideSkipButton();
  }

  dimNumber(number: number): void {
    if (this.numberEl) {
      this.numberEl.textContent = String(number);
      this.numberEl.className = 'display__number-text display__number-text--dimmed';
    }
  }

  showSkipButton(): void {
    if (this.skipBtn) {
      this.skipBtn.style.display = 'inline-block';
      this.skipBtn.textContent = this.i18n.t(I18N_KEYS.DISPLAY_SKIP_AUDIO);
    }
  }

  hideSkipButton(): void {
    if (this.skipBtn) {
      this.skipBtn.style.display = 'none';
    }
  }

  showProgress(elapsed: number, total: number | null): void {
    if (!this.progressEl) return;
    this.progressEl.style.display = 'block';
    const fill = this.progressEl.querySelector('.display__progress-fill') as HTMLElement;
    if (fill && total && total > 0) {
      const pct = Math.min(100, (elapsed / total) * 100);
      fill.style.width = `${pct}%`;
    }
  }

  hideProgress(): void {
    if (this.progressEl) {
      this.progressEl.style.display = 'none';
    }
  }

  updateLabels(): void {
    if (this.skipBtn) {
      this.skipBtn.textContent = this.i18n.t(I18N_KEYS.DISPLAY_SKIP_AUDIO);
    }
    // Update ready text if currently showing ready state
    if (this.numberEl && this.numberEl.classList.contains('display__number-text--ready')) {
      this.numberEl.textContent = this.i18n.t(I18N_KEYS.DISPLAY_READY);
    }
    // Update playing status if currently showing playing state
    if (this.statusEl && this.statusEl.classList.contains('display__status--playing')) {
      this.statusEl.textContent = this.i18n.t(I18N_KEYS.DISPLAY_PLAYING);
    }
  }
}
